import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import MuiPagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

const BRAND = "#4f46e5";
const BRAND_HOVER = "#4338ca";
const TEXT_MUTED = "#64748b";
const BORDER = "#e5e7eb";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

const PaginationRoot = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem 1rem",
  width: "100%",
  boxSizing: "border-box",
}));

const SummaryText = styled(Typography)(() => ({
  margin: 0,
  fontSize: "0.875rem",
  color: TEXT_MUTED,
  lineHeight: 1.4,
}));

const Controls = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "0.75rem",
}));

const PageSizeWrapper = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.5rem",
}));

const PageSizeLabel = styled(Typography)(() => ({
  fontSize: "0.875rem",
  color: TEXT_MUTED,
  whiteSpace: "nowrap",
}));

const PageSizeControl = styled(FormControl)(() => ({
  minWidth: "4.5rem",
}));

const PageSizeSelect = styled(Select)(() => ({
  fontSize: "0.875rem",
  color: BRAND,
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6366f1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: BRAND,
  },
  "& .MuiSelect-select": {
    padding: "0.375rem 2rem 0.375rem 0.625rem",
  },
}));

const StyledPagination = styled(MuiPagination)(() => ({
  "& .MuiPaginationItem-root": {
    color: BRAND,
    border: `1px solid ${BORDER}`,
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: 500,
    minWidth: "2rem",
    height: "2rem",
    margin: "0 2px",
    "&:hover": {
      backgroundColor: "#eef2ff",
      borderColor: "#c7d2fe",
    },
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: BRAND,
    color: "#ffffff",
    borderColor: BRAND,
    "&:hover": {
      backgroundColor: BRAND_HOVER,
      borderColor: BRAND_HOVER,
    },
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    color: "#64748b",
    borderColor: "#e5e7eb",
    opacity: 1,
  },
  "& .MuiPaginationItem-ellipsis": {
    border: "none",
  },
}));

const Pagination = ({
  page = 1,
  totalPages = 1,
  onPageChange,
  disabled = false,
  className = "",
  siblingCount = 1,
  boundaryCount = 1,
  showFirstButton = true,
  showLastButton = true,
  showSummary = true,
  totalItems,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  summaryLabel,
  size = "medium",
  shape = "rounded",
  "aria-label": ariaLabel = "Pagination navigation",
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);
  const showPageSizeSelect =
    typeof onPageSizeChange === "function" && pageSizeOptions?.length > 0;

  const summaryText = useMemo(() => {
    if (summaryLabel) {
      return summaryLabel;
    }

    if (
      typeof totalItems === "number" &&
      typeof pageSize === "number" &&
      pageSize > 0
    ) {
      if (totalItems === 0) {
        return "No results";
      }

      const start = (safePage - 1) * pageSize + 1;
      const end = Math.min(safePage * pageSize, totalItems);
      return `Showing ${start}–${end} of ${totalItems}`;
    }

    return `Page ${safePage} of ${safeTotalPages}`;
  }, [summaryLabel, totalItems, pageSize, safePage, safeTotalPages]);

  const handleChange = (_event, value) => {
    if (disabled || value === safePage) {
      return;
    }
    onPageChange?.(value);
  };

  if (safeTotalPages <= 1 && !showPageSizeSelect && !showSummary) {
    return null;
  }

  return (
    <PaginationRoot className={className} component="nav" aria-label={ariaLabel}>
      {showSummary && <SummaryText component="p">{summaryText}</SummaryText>}

      <Controls>
        {showPageSizeSelect && (
          <PageSizeWrapper>
            <PageSizeLabel component="span">Rows per page</PageSizeLabel>
            <PageSizeControl size="small" variant="outlined">
              <PageSizeSelect
                value={pageSize}
                disabled={disabled}
                onChange={(event) =>
                  onPageSizeChange(Number(event.target.value))
                }
                inputProps={{ "aria-label": "Rows per page" }}
              >
                {pageSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </PageSizeSelect>
            </PageSizeControl>
          </PageSizeWrapper>
        )}

        {safeTotalPages > 1 && (
          <StyledPagination
            count={safeTotalPages}
            page={safePage}
            onChange={handleChange}
            disabled={disabled}
            siblingCount={siblingCount}
            boundaryCount={boundaryCount}
            showFirstButton={showFirstButton}
            showLastButton={showLastButton}
            size={size}
            shape={shape}
            color="primary"
          />
        )}
      </Controls>
    </PaginationRoot>
  );
};

export default Pagination;
